import { SearchIcon } from 'core/components/Icons'
import { Modal } from 'core/components/ui/Modal'
import axiosInstance from 'core/network/axiosInstance'
import { orderRepository } from 'features/orders/data/repositories/OrderRepositoryImpl'
import { OrderListItem } from 'features/orders/domain/entities/Order'
import React, { useEffect, useRef, useState } from 'react'
import { PaymentRecord } from '../../domain/entities/Payment'

interface RecordOfflinePaymentModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: Omit<PaymentRecord, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'buyer'>) => Promise<void>
  pendingOrders?: { id: string; totalAmount: number }[]
}

export function RecordOfflinePaymentModal({
  isOpen,
  onClose,
  onSubmit,
  pendingOrders = [],
}: RecordOfflinePaymentModalProps) {
  const [orderId, setOrderId] = useState('')
  const [selectedOrder, setSelectedOrder] = useState<{
    id: string
    totalAmount: number
    buyerName?: string
  } | null>(null)
  const [amount, setAmount] = useState<number>(0)
  const [paymentMode, setPaymentMode] = useState<'cash' | 'upi'>('cash')
  const [offlineRef, setOfflineRef] = useState('')
  const [screenshotUrl, setScreenshotUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  // Combobox async search state
  const [searchQuery, setSearchQuery] = useState('')
  const [orderOptions, setOrderOptions] = useState<OrderListItem[]>([])
  const [searching, setSearching] = useState(false)
  const [comboboxOpen, setComboboxOpen] = useState(false)
  const comboboxRef = useRef<HTMLDivElement>(null)

  // Reset modal state on open
  useEffect(() => {
    if (isOpen) {
      setOrderId('')
      setSelectedOrder(null)
      setAmount(0)
      setPaymentMode('cash')
      setOfflineRef('')
      setScreenshotUrl('')
      setErrorMsg('')
      setSearchQuery('')
      setComboboxOpen(false)
      fetchInitialOrders()
    }
  }, [isOpen])

  // Fetch initial top pending indents
  const fetchInitialOrders = async () => {
    setSearching(true)
    try {
      const res = await orderRepository.getOrders({ status: 'order_placed', page: 1, limit: 15 })
      setOrderOptions(res.orders)
    } catch (err) {
      // Fallback to prop array if backend fetch fails
      setOrderOptions([])
    } finally {
      setSearching(false)
    }
  }

  // Debounced search when user types in combobox
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const timer = setTimeout(() => {
      setSearching(true)
      orderRepository
        .getOrders({ status: 'order_placed', search: searchQuery || undefined, page: 1, limit: 15 })
        .then((res) => setOrderOptions(res.orders))
        .catch(() => setOrderOptions([]))
        .finally(() => setSearching(false))
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, isOpen])

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target as Node)) {
        setComboboxOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const getBuyerName = (item: OrderListItem | { id: string; totalAmount?: number; buyerName?: string }) => {
    if ('buyer' in item && item.buyer) {
      return item.buyer.businessName || item.buyer.name
    }

    if ('buyerName' in item) {
      return item.buyerName
    }

    return undefined
  }

  const handleSelectOrder = (item: OrderListItem | { id: string; totalAmount?: number; buyerName?: string }) => {
    const totalVal = item.totalAmount || 0
    const bName = getBuyerName(item)

    setOrderId(item.id)
    setAmount(totalVal)
    setSelectedOrder({ id: item.id, totalAmount: totalVal, buyerName: bName })
    setComboboxOpen(false)
    setSearchQuery('')
  }

  const handleClearSelectedOrder = () => {
    setOrderId('')
    setSelectedOrder(null)
    setAmount(0)
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    setErrorMsg('')

    try {
      const { data: presigned } = await axiosInstance.post('/api/v1/storage/presigned-url', {
        file_name: file.name,
        content_type: file.type,
        category: 'payments',
      })

      const uploadRes = await fetch(presigned.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })

      if (!uploadRes.ok) {
        throw new Error(`Failed to upload ${file.name}. Status: ${uploadRes.status}`)
      }

      setScreenshotUrl(presigned.public_url)
    } catch (err: unknown) {
      const errorVal = err as Error
      setErrorMsg(errorVal.message || 'Image upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!orderId || amount <= 0 || !offlineRef) {
      setErrorMsg('All fields are required and amount must be positive.')
      return
    }

    setSubmitting(true)
    setErrorMsg('')
    try {
      await onSubmit({
        orderId,
        amount,
        paymentMode,
        qrRef: offlineRef,
        screenshotUrl: screenshotUrl || undefined,
        verifiedAt: new Date().toISOString(),
      })
      onClose()
    } catch (err: unknown) {
      const errorVal = err as Error
      setErrorMsg(errorVal.message || 'Failed to register offline payment.')
    } finally {
      setSubmitting(false)
    }
  }

  const footerActions = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={submitting || uploading}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="record-offline-form"
        disabled={submitting || uploading || !orderId || amount <= 0 || !offlineRef}
        className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-fg rounded-btn transition-colors disabled:opacity-50 min-w-[5rem] flex items-center justify-center"
      >
        {submitting ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          'Record Payment'
        )}
      </button>
    </>
  )

  const renderComboboxDropdownContent = () => {
    if (orderOptions.length > 0) {
      return orderOptions.map((o) => (
        <div
          key={o.id}
          onClick={() => handleSelectOrder(o)}
          className="p-2.5 hover:bg-bg cursor-pointer transition-colors flex items-center justify-between gap-2 text-left"
        >
          <div className="min-w-0 flex-1">
            <span className="font-mono font-bold text-text-primary block truncate">{o.id}</span>
            <span className="text-[10px] text-text-secondary font-medium block truncate">
              {o.buyer?.businessName || o.buyer?.name || 'Buyer'}
              {o.buyer?.phone ? ` • ${o.buyer.phone}` : ''}
            </span>
          </div>
          <span className="font-bold text-primary shrink-0 text-xs">
            ₹{(o.totalAmount || 0).toLocaleString('en-IN')}
          </span>
        </div>
      ))
    }

    if (pendingOrders.length > 0) {
      return pendingOrders.map((o) => (
        <div
          key={o.id}
          onClick={() => handleSelectOrder(o)}
          className="p-2.5 hover:bg-bg cursor-pointer transition-colors flex items-center justify-between gap-2 text-left"
        >
          <span className="font-mono font-bold text-text-primary truncate">{o.id}</span>
          <span className="font-bold text-primary shrink-0 text-xs">
            ₹{(o.totalAmount || 0).toLocaleString('en-IN')}
          </span>
        </div>
      ))
    }

    const emptyMessage = searching ? 'Searching orders...' : 'No pending indents found.'
    return <div className="p-3 text-center text-text-secondary font-medium">{emptyMessage}</div>
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Record Offline Payment" footer={footerActions} size="md">
      <form id="record-offline-form" onSubmit={handleSubmit} className="space-y-4 text-left">
        {errorMsg && (
          <div className="p-3 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
            {errorMsg}
          </div>
        )}

        {/* Order Selector (Combobox) */}
        <div className="space-y-1.5 relative" ref={comboboxRef}>
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
              Linked Indent / Order ID <span className="text-danger">*</span>
            </label>
            {selectedOrder && (
              <button
                type="button"
                onClick={handleClearSelectedOrder}
                className="text-[11px] font-semibold text-primary hover:underline"
              >
                Change Indent
              </button>
            )}
          </div>

          {selectedOrder ? (
            /* Selected Order Display Card */
            <div className="flex items-center justify-between p-3 border border-primary/30 bg-primary/5 rounded-btn">
              <div className="flex flex-col">
                <span className="font-mono font-bold text-primary text-xs">{selectedOrder.id}</span>
                {selectedOrder.buyerName && (
                  <span className="text-xs text-text-secondary">{selectedOrder.buyerName}</span>
                )}
              </div>
              <div className="text-right">
                <span className="text-xs text-text-secondary block">Payable Amount</span>
                <span className="font-bold text-text-primary text-xs">
                  ₹{selectedOrder.totalAmount.toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          ) : (
            /* Async Search Combobox Input */
            <div className="relative">
              <input
                type="text"
                placeholder="Type Order ID or Buyer Name to search..."
                value={searchQuery}
                onFocus={() => setComboboxOpen(true)}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setComboboxOpen(true)
                }}
                className="w-full pl-9 pr-8 py-2 border border-border rounded-btn bg-bg text-text-primary text-xs font-medium focus:border-primary focus:outline-none transition-colors"
              />
              <SearchIcon className="absolute left-3 top-2.5 h-3.5 w-3.5 text-text-secondary pointer-events-none" />
              {searching && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-primary">
                  <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              )}
            </div>
          )}

          {/* Async Combobox Dropdown Results List */}
          {comboboxOpen && !selectedOrder && (
            <div className="absolute left-0 right-0 z-30 mt-1 max-h-56 overflow-y-auto rounded-card border border-border bg-surface shadow-card p-1 text-xs divide-y divide-border">
              {renderComboboxDropdownContent()}
            </div>
          )}
        </div>

        {/* Payment Mode Selector */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider">Payment Mode</label>
          <select
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value as 'upi' | 'cash')}
            className="w-full px-3 py-2 border border-border rounded-btn bg-bg text-text-primary text-xs font-medium focus:border-primary focus:outline-none transition-colors"
          >
            <option value="cash">Cash Payment</option>
            <option value="upi">UPI / Online Transfer</option>
          </select>
        </div>

        {/* Amount Input */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider">Amount Received (₹)</label>
          <input
            type="number"
            value={amount || ''}
            onChange={(e) => setAmount(Number(e.target.value))}
            placeholder="e.g. 50000"
            className="w-full px-3.5 py-2 border border-border rounded-btn bg-bg text-text-primary text-xs font-medium focus:border-primary focus:outline-none transition-colors"
          />
        </div>

        {/* Offline Reference */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
            Offline Receipt Reference Code / Voucher No.
          </label>
          <input
            type="text"
            value={offlineRef}
            onChange={(e) => setOfflineRef(e.target.value)}
            placeholder="e.g. CASH-REC-00234 or UTR-9876543"
            className="w-full px-3.5 py-2 border border-border rounded-btn bg-bg text-text-primary text-xs font-medium focus:border-primary focus:outline-none transition-colors"
          />
        </div>

        {/* Payment Receipt / Proof Upload */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
            Upload Receipt Proof / Screenshot (Optional)
          </label>

          {screenshotUrl ? (
            <div className="relative group rounded-card overflow-hidden border border-border bg-bg/40 h-28 w-28 p-1">
              <img src={screenshotUrl} alt="Receipt proof" className="h-full w-full object-cover rounded-btn" />
              <button
                type="button"
                onClick={() => setScreenshotUrl('')}
                className="absolute top-2 right-2 h-6 w-6 rounded-full bg-danger text-white flex items-center justify-center font-bold text-xs shadow hover:bg-danger-hover transition-colors"
                title="Remove image"
              >
                ✕
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-border hover:border-primary/50 rounded-card cursor-pointer bg-bg/40 hover:bg-bg/80 transition-all text-center">
              {uploading ? (
                <div className="flex items-center gap-2 text-xs font-bold text-primary">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Uploading Image...</span>
                </div>
              ) : (
                <div className="space-y-1 text-xs text-text-secondary">
                  <svg
                    className="mx-auto h-6 w-6 text-text-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="font-semibold text-text-primary block">Click to upload payment receipt</span>
                  <span className="text-[10px] block">PNG, JPG, or WEBP up to 10MB</span>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} className="hidden" />
            </label>
          )}
        </div>
      </form>
    </Modal>
  )
}
